/* eslint-disable */
import { useState, useCallback } from 'react';
import { apiCaller } from 'utils/axios';
import { logger } from 'utils/environment/log';

/**
 * @typedef {Object} Bank
 * @property {string} id
 * @property {string} name
 */

/**
 * @typedef {Object} PaymentDetail
 * @property {number} id
 * @property {Bank} bank
 * @property {string|null} branchCode
 * @property {string} accountNumber
 * @property {string|null} phoneNumber
 */

/**
 * @typedef {Object} User
 * @property {string} id
 * @property {PaymentDetail} paymentDetail
 * @property {string} email
 * @property {string} employeeNumber
 * @property {string} employeeTitle
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} status
 */

export const useTravellerSearch = () => {
    const [searchResults, setSearchResults] = useState([]);
    const [selectedTravellers, setSelectedTravellers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const searchTravellers = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) return;

        setLoading(true);
        setError(null);

        try {
            const response = await apiCaller.get(`/api/users`, {
                params: {
                    email: searchTerm,
                    page: 0,
                    size: 20
                }
            });

            // Map the response data to include only necessary fields and handle nested objects
            const mappedUsers = (response.content || []).map(user => ({
                id: user.id,
                email: user.email,
                employeeNumber: user.employeeNumber,
                employeeTitle: user.employeeTitle,
                firstName: user.firstName,
                lastName: user.lastName,
                status: user.status,
                paymentDetail: user.paymentDetail ? {
                    id: user.paymentDetail.id,
                    accountNumber: user.paymentDetail.accountNumber,
                    bank: user.paymentDetail.bank ? {
                        id: user.paymentDetail.bank.id,
                        name: user.paymentDetail.bank.name
                    } : null,
                    branchCode: user.paymentDetail.branchCode,
                    phoneNumber: user.paymentDetail.phoneNumber
                } : null
            }));

            setSearchResults(mappedUsers);
        } catch (err) {
            logger.error('Error searching travellers:', err);
            setError('Failed to search travellers. Please try again.');
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleTravellerSelection = useCallback((index, selectedTraveller, replace) => {
        if (selectedTraveller) {
            // Validate user status before selection
            if (selectedTraveller.status === 'DISABLED') {
                setError(`Cannot select ${selectedTraveller.firstName} ${selectedTraveller.lastName} - User is disabled`);
                return;
            }

            setSelectedTravellers((prev) => {
                const newTravellers = [...prev];
                newTravellers[index] = selectedTraveller;
                return newTravellers.filter(Boolean);
            });
            replace(index, selectedTraveller.id);
        } else {
            setSelectedTravellers((prev) => prev.filter((_, idx) => idx !== index));
            replace(index, '');
        }
        setError(null);
    }, []);

    const resetTravellerSearch = useCallback(() => {
        setSearchResults([]);
        setSelectedTravellers([]);
        setError(null);
    }, []);

    return {
        searchResults,
        selectedTravellers,
        loading,
        error,
        searchTravellers,
        handleTravellerSelection,
        resetTravellerSearch
    };
}; 